import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, CheckCircle } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";
import ContextCarryOver from "@/components/ContextCarryOver";

const STEPS = [
  { n: 1, code: "1", key: "aspiration", label: "Winning Aspiration", phase: "STRATEGY", placeholder: "Define the overarching purpose, guiding mission, and what winning looks like." },
  { n: 2, code: "2", key: "whereToPlay", label: "Where to Play", phase: "STRATEGY", placeholder: "Target demographics, geographies, channels — and what space to explicitly ignore." },
  { n: 3, code: "3", key: "howToWin", label: "How to Win", phase: "STRATEGY", placeholder: "The distinct competitive advantage, value proposition, or structural barrier to entry." },
  { n: 4, code: "4", key: "capabilities", label: "Core Capabilities", phase: "STRATEGY", placeholder: "The configuration of activities, skills and resources required to deliver the How to Win." },
  { n: 5, code: "5", key: "systems", label: "Management Systems", phase: "STRATEGY", placeholder: "Metrics, governance and operational processes required to sustain the capabilities." },
];
const LAST = STEPS.length;
const empty = { aspiration: "", whereToPlay: "", howToWin: "", capabilities: "", systems: "" };
const taCls = "w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent";

export default function PlayingToWin() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [data, setData] = useState(empty);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/documents/${id}`);
        setTitle(res.data.title || "");
        setData({ ...empty, ...(res.data.payload || {}) });
      } catch { toast.error("Document not found"); navigate("/dashboard"); }
      finally { setLoading(false); }
    })();
  }, [id, navigate]);

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try { await api.put(`/documents/${id}`, { title: title || "Untitled Strategy Cascade", payload: data, ...extra }); return true; }
    catch { toast.error("Save failed"); return false; }
    finally { setSaving(false); }
  }, [id, title, data]);

  const cur = STEPS[step - 1];
  const set = (v) => setData((d) => ({ ...d, [cur.key]: v }));
  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const next = async () => { await persist(); setStep((s) => Math.min(LAST, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const finalise = async () => { if (await persist({ status: "Completed" })) { toast.success("Strategy cascade finalised"); navigate(`/ptw/${id}/view`); } };
  const applySuggestion = (text) => { setData((d) => { const e = d[cur.key]; return { ...d, [cur.key]: !e || !e.trim() ? text : `${e.trimEnd()}\n- ${text}` }; }); toast.success("Suggestion added"); };
  const onPull = ({ title: t, context }) => { setData((d) => ({ ...d, aspiration: context || d.aspiration })); if (!title && t) setTitle(t); toast.success("Context pulled in"); };

  if (loading) return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={16} /> Workspace</button>
        <input data-testid="ptw-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Strategy cascade title…"
          className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="ptw-stepper">
          {STEPS.map((s) => (
            <button key={s.n} data-testid={`step-${s.n}`} onClick={() => setStep(s.n)}
              className={`rounded-sm border px-3 py-2.5 text-left transition-all ${step === s.n ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className={`font-mono text-xs font-bold ${step === s.n ? "text-indigo-700" : "text-slate-400"}`}>STEP {s.n}</span>
              <p className={`text-xs font-medium ${step === s.n ? "text-slate-900" : "text-slate-500"}`}>{s.label}</p>
            </button>
          ))}
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-4">
          <h2 className="text-xl font-semibold text-slate-900">{cur.label}</h2>
          {step === 1 && <ContextCarryOver currentId={id} onPull={onPull} />}
          <textarea data-testid={`field-${cur.key}`} rows={9} className={taCls} value={data[cur.key]} onChange={(e) => set(e.target.value)} placeholder={cur.placeholder} />
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="ptw-back" onClick={back} disabled={step === 1} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"><ArrowLeft size={16} /> Back</button>
          <div className="flex items-center gap-3">
            <button data-testid="ptw-save-draft" onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">{saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft</button>
            {step < LAST ? (
              <button data-testid="ptw-next" onClick={next} className="inline-flex items-center gap-2 rounded-sm bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700">Next Step <ArrowRight size={16} /></button>
            ) : (
              <button data-testid="ptw-finalise" onClick={finalise} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700"><CheckCircle size={18} weight="fill" /> Finalise Cascade</button>
            )}
          </div>
        </div>
      </div>

      <CoPilotToggle open={open} onClick={() => setOpen(true)} phase={cur.phase} />
      <CoPilotPanel open={open} onClose={() => setOpen(false)} docId={id} toolType="playing_to_win" step={cur} phase={cur.phase} onApplySuggestion={applySuggestion} />
    </Layout>
  );
}
